import {
  FloatingFocusManager,
  FloatingPortal,
  useMergeRefs,
} from '@floating-ui/react';
import {
  memo,
  useLayoutEffect,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

import { usePopoverContext } from '../context';
import {
  CARET_HALF_WIDTH,
  computeCaretOffset,
  POPOVER_BORDER_RADIUS,
  resolveCaretEdge,
  type CaretEdge,
} from '../utils';

type Props = {
  children: ReactNode;
  /** Accessible name for the popover, announced when it opens. */
  label: string;
  /** Hides the caret that points at the trigger. @default false */
  disableCaret?: boolean;
};

// Keyboard Enter/Space synthesize a click with detail 0; a real press is > 0.
function isPointerDriven(event: Event | undefined) {
  return event instanceof MouseEvent && event.detail > 0;
}

function resolveCaretAxis(edge: CaretEdge) {
  return edge === 'top' || edge === 'bottom' ? 'horizontal' : 'vertical';
}

function resolveCaretStyle(edge: CaretEdge, offset: number): CSSProperties {
  if (edge === 'top' || edge === 'bottom') {
    return { left: offset };
  }

  return { top: offset };
}

export const PopoverContent = memo(
  ({ children, label, disableCaret = false }: Props) => {
    const { context, floatingStyles, getFloatingProps, isOpen, setFloating } =
      usePopoverContext();
    const [caretOffset, setCaretOffset] = useState<number | null>(null);
    const [popoverElement, setPopoverElement] = useState<HTMLDivElement | null>(
      null,
    );
    const popoverRef = useMergeRefs<HTMLDivElement>([
      setFloating,
      setPopoverElement,
    ]);

    const caretEdge = resolveCaretEdge(context.placement);
    const caretAxis = resolveCaretAxis(caretEdge);

    useLayoutEffect(() => {
      const reference = context.elements.reference;
      if (disableCaret || !popoverElement || !reference) {
        setCaretOffset(null);
        return;
      }

      const offset = computeCaretOffset({
        axis: caretAxis,
        borderRadius: POPOVER_BORDER_RADIUS,
        caretHalfWidth: CARET_HALF_WIDTH,
        popoverRect: popoverElement.getBoundingClientRect(),
        triggerRect: reference.getBoundingClientRect(),
      });

      // `position: absolute` resolves against the padding box, so the caret
      // starts one border width in from the rect the offset was measured on.
      const borderInset =
        caretAxis === 'horizontal'
          ? popoverElement.clientLeft
          : popoverElement.clientTop;

      setCaretOffset(Math.round(offset - borderInset));
    }, [
      caretAxis,
      context.elements.reference,
      context.x,
      context.y,
      disableCaret,
      popoverElement,
    ]);

    if (!isOpen) {
      return null;
    }

    // Pointer open: keep focus on the trigger so :focus-visible does not paint.
    const isPointerOpen = isPointerDriven(context.dataRef.current.openEvent);

    return (
      <FloatingPortal>
        <FloatingFocusManager
          context={context}
          initialFocus={isPointerOpen ? -1 : 0}
          modal={false}
          returnFocus={true}
        >
          <div
            aria-label={label}
            className="eds-popover"
            data-placement={context.placement}
            ref={popoverRef}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            {caretOffset != null ? (
              <div
                className={`eds-popover--caret eds-popover--caret--edge-${caretEdge}`}
                style={resolveCaretStyle(caretEdge, caretOffset)}
              >
                <span className="eds-popover--caret--border" />
                <span className="eds-popover--caret--fill" />
              </div>
            ) : null}
            <div className="eds-popover--body">{children}</div>
          </div>
        </FloatingFocusManager>
      </FloatingPortal>
    );
  },
);

PopoverContent.displayName = 'PopoverContent';
